import { useEffect, useState } from "react";
import { Form, Input, Select, DatePicker, message } from "antd";
import LoadingButton from '@mui/lab/LoadingButton';
import GenericModal from "../../../components/buttons/genericModal";
import { backendPatchWithoutRefresh } from "../../../helpers/callBackend";
import moment from 'moment';

const EditGeneralLedgerRule = ({
    onSuccess,
    title,
    glRuleId,
    currentName,
    currentDescription,
    currentSearchTerm,
    currentGroupingType,
    currentGroupId,
    currentGlAccountId,
    currentCustomStartDate,
    currentCustomEndDate,
    groupingTypeFilter,
    selectGroupFilter,
    setSelectGroup,
    setGroupingType,
    currentGroupName,
    setSelectGroupFilter,
    generalLedgerAccount,
    loadingGroupingOptions,
}) => {
    const [visible, setVisible] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [form] = Form.useForm();

    useEffect(() => {
        setSelectGroupFilter(prevState => {
            const groupExists = prevState.some(group => group.id === currentGroupId);
            if (!groupExists) {
                return [...prevState, {
                    id: currentGroupId,
                    name: currentGroupName,
                }];
            }
            return prevState;
        });
        setGroupingType(currentGroupingType);
        setSelectGroup(currentGroupId);
    }, []);

    useEffect(() => {
        form.setFieldsValue({
            name: currentName || '',
            description: currentDescription || '',
            search_term: currentSearchTerm || '',
            grouping_type: currentGroupingType || undefined,
            group_id: currentGroupId || undefined,
            gl_account_id: currentGlAccountId || undefined,
            date_range: currentCustomStartDate && currentCustomEndDate
                ? [moment(currentCustomStartDate), moment(currentCustomEndDate)]
                : null,
        });
    }, [form, currentName, currentDescription, currentSearchTerm, currentGroupingType, currentGroupId, currentGlAccountId, currentCustomStartDate, currentCustomEndDate]);

    const onFormSubmit = (values) => {
        setSubmitting(true);

        const hasCustomStartDate = values?.date_range?.length === 2 && values.date_range[0];
        const hasCustomEndDate = values?.date_range?.length === 2 && values.date_range[1];

        if ((hasCustomStartDate && !hasCustomEndDate) || (!hasCustomStartDate && hasCustomEndDate)) {
            message.error("Both custom start date and custom end date must be provided.");
            setSubmitting(false);
            return;
        }

        const data = {
            id: glRuleId,
            ...values,
            ...(hasCustomStartDate && hasCustomEndDate
                ? {
                    custom_start_date: moment.utc(values.date_range[0]).format('YYYY-MM-DD HH:mm:ss.SSSSSS'),
                    custom_end_date: moment.utc(values.date_range[1]).format('YYYY-MM-DD HH:mm:ss.SSSSSS'),
                }
                : {})
        };

        backendPatchWithoutRefresh("/api/general-ledger/rules/", data)
            .then((res) => {
                setVisible(false);
                onSuccess(res?.data?.general_ledger_tag_rule);
            })
            .catch((error) => {
                console.error('Error:', error);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };


    return (
        <GenericModal
            visible={visible}
            handleCancelModal={() => setVisible(false)}
            showModal={() => setVisible(true)}
            size="md"
            button={<span style={{ color: "#1890ff" }}> Edit </span>}
            modalTitle={title}
            modalBody={
                <Form
                    form={form}
                    layout="inline"
                    colon={false}
                    onFinish={onFormSubmit}
                >
                    <Form.Item
                        label={<label style={{ marginRight: 121 }}> Rule Name: </label>}
                        name="name"
                        style={{ marginBottom: 10 }}
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Enter rule name" style={{ width: 300 }} />
                    </Form.Item>

                    <Form.Item
                        label={<label style={{ marginRight: 116 }}> Description: </label>}
                        name="description"
                        style={{ marginBottom: 10 }}
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Enter description" style={{ width: 300 }} />
                    </Form.Item>

                    <Form.Item
                        label={<label style={{ marginRight: 109 }}> Search Term: </label>}
                        name="search_term"
                        style={{ marginBottom: 10 }}
                        rules={[{ required: true }]}
                    >
                        <Input placeholder="Enter search term (regex)" style={{ width: 300 }} />
                    </Form.Item>

                    <Form.Item
                        label={<label style={{ marginRight: 73 }}> Account Grouping: </label>}
                        name="grouping_type"
                        rules={[{ required: true }]}
                        style={{ marginBottom: 10 }}
                    >
                        <Select
                            style={{ width: 300 }}
                            dropdownMatchSelectWidth={false}
                            placeholder="Select Account Grouping"
                            onChange={(e) => {
                                setGroupingType(e);
                                form.setFieldsValue({ group_id: undefined });
                            }}
                        >
                            {groupingTypeFilter.map((option) => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<label style={{ marginRight: 149 }}> Group: </label>}
                        name="group_id"
                        rules={[{ required: true }]}
                        style={{ marginBottom: 10 }}
                    >
                        <Select
                            style={{ width: 300 }}
                            disabled={selectGroupFilter.length === 0 || loadingGroupingOptions}
                            loading={loadingGroupingOptions}
                            placeholder="Select Group"
                            dropdownMatchSelectWidth={false}
                            onChange={(e) => setSelectGroup(e)}
                        >
                            {selectGroupFilter.map((option) => (
                                <Select.Option key={option.id} value={option.id}>
                                    {option.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<label style={{ marginRight: 109 }}> G/L Account: </label>}
                        name="gl_account_id"
                        rules={[{ required: true }]}
                        style={{ marginBottom: 10 }}
                    >
                        <Select
                            style={{ width: 300 }}
                            dropdownMatchSelectWidth={false}
                            placeholder="Select G/L Account"
                        >
                            {generalLedgerAccount.map((option) => (
                                <Select.Option key={option.id} value={option.id}>
                                    {option.code} {option.description}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        label={<label style={{ marginRight: 80 }}> Date Range: </label>}
                        name="date_range"
                        rules={[{ required: true }]}
                    >
                        <DatePicker.RangePicker style={{ width: 300, marginLeft: 33 }} />
                    </Form.Item>

                    <Form.Item>
                        <LoadingButton
                            disableElevation
                            fullWidth
                            size="small"
                            type="submit"
                            variant="contained"
                            color="primary"
                            loading={submitting}
                            loadingPosition="start"
                            sx={{ fontSize: 14, textTransform: 'none' }}
                            style={{ margin: "3px", marginTop: "20px", width: "100px" }}
                        >
                            Submit
                        </LoadingButton>
                    </Form.Item>
                </Form>
            }
        />
    );
};

export default EditGeneralLedgerRule;
